import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { ArrowRight, Calendar, CalendarClock, Check, ChevronRight, CircleDot, Flag, Hourglass, Pause, Play, Tag, Timer, UsersRound } from '@/components/ui/icons';
import type { Task } from '@shared/types';
import { isCompleteType } from '@shared/constants';
import { useDb, useDbShallow, useMe } from '@/lib/store';
import { effectiveStatuses, statusOf } from '@/lib/hierarchy';
import { addTimeEntry, advanceStatus, completeTask, runningTimer, startTimer, stopTimer, trackedMs, updateTask } from '@/lib/actions';
import { dueTone, formatDue, formatDuration, parseDuration } from '@/lib/format';
import { Popover } from '@/components/ui/Popover';
import { Tooltip } from '@/components/ui/Tooltip';
import { AvatarStack } from '@/components/ui/Avatar';
import { StatusMenu, StatusPill } from '@/components/fields/Status';
import { PeopleMenu } from '@/components/fields/People';
import { PriorityFlag, PriorityMenu } from '@/components/fields/Priority';
import { DateRangeMenu } from '@/components/fields/DatePicker';
import { TagChip, TagMenu } from '@/components/fields/Tags';
import { TimeEstimateEditor } from '@/components/fields/TaskCells';
import { readableOn, isNeutral } from '@/lib/color';

function Prop({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="tm-prop">
      <div className="tm-prop-label">
        {icon}
        <span>{label}</span>
      </div>
      <div className="tm-prop-value">{children}</div>
    </div>
  );
}

function StatusControl({ task }: { task: Task }) {
  const status = useDb((db) => statusOf(db, task));
  const statuses = useDbShallow((db) => effectiveStatuses(db, task.listId));
  const closed = isCompleteType(status.type);
  const idx = statuses.findIndex((s) => s.id === status.id);
  const next = statuses[idx + 1];
  const fg = isNeutral(status.color) ? 'var(--text-1)' : readableOn(status.color);
  return (
    <div className="tm-status">
      <Popover
        content={(close) => (
          <StatusMenu
            statuses={statuses}
            currentId={task.statusId}
            onPick={(s) => {
              updateTask(task.id, { statusId: s.id });
              close();
            }}
          />
        )}
      >
        <button className="tm-status-pill">
          <StatusPill status={status} statuses={statuses} size="lg" iconAfter />
        </button>
      </Popover>
      {next && (
        <Tooltip label={`Next status: ${next.name.toUpperCase()}`}>
          <button className={clsx('tm-status-next', { neutral: isNeutral(status.color) })} style={isNeutral(status.color) ? undefined : { background: status.color, color: fg }} onClick={() => advanceStatus(task.id)}>
            <ChevronRight size={14} />
          </button>
        </Tooltip>
      )}
      <Tooltip label={closed ? 'Reopen task' : 'Mark complete'}>
        <button className={clsx('tm-status-done', { on: closed })} onClick={() => completeTask(task.id)}>
          <Check size={14} />
        </button>
      </Tooltip>
    </div>
  );
}

function TrackTime({ task }: { task: Task }) {
  const me = useMe();
  const running = useDb((db) => runningTimer(db, me));
  const total = useDb((db) => trackedMs(db, task.id));
  const isRunning = running?.taskId === task.id;
  const [, tick] = useState(0);
  useEffect(() => {
    if (!isRunning) return;
    const t = setInterval(() => tick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [isRunning]);
  const [manual, setManual] = useState('');
  return (
    <div className="row gap-8">
      <button className={clsx('tm-timer', { running: isRunning })} onClick={() => (isRunning ? stopTimer() : startTimer(task.id))}>
        {isRunning ? <Pause size={12} fill="currentColor" /> : <Play size={12} fill="currentColor" />}
        <span>{isRunning ? formatDuration(Date.now() - running!.start, { seconds: true }) : total ? formatDuration(total) : 'Start'}</span>
      </button>
      <Popover
        content={(close) => (
          <div className="menu" style={{ width: 240 }}>
            <div className="menu-label">Add time manually</div>
            <input
              className="input"
              autoFocus
              placeholder="e.g. 1h 30m"
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const ms = parseDuration(manual);
                  if (ms) addTimeEntry(task.id, ms);
                  setManual('');
                  close();
                }
              }}
            />
            {total > 0 && (
              <div className="faint" style={{ fontSize: 'var(--fs-sm)', padding: '8px 2px 2px' }}>
                Total tracked: {formatDuration(total)}
              </div>
            )}
          </div>
        )}
      >
        <button className="tm-empty-btn">{total && !isRunning ? 'Add time' : ''}</button>
      </Popover>
    </div>
  );
}

export function TaskProperties({ task }: { task: Task }) {
  const closed = useDb((db) => isCompleteType(statusOf(db, task).type));
  const spaceId = useDb((db) => db.lists[task.listId]?.spaceId ?? '');
  return (
    <div className="tm-props">
      <Prop icon={<CircleDot size={15} />} label="Status">
        <StatusControl task={task} />
      </Prop>
      <Prop icon={<UsersRound size={15} />} label="Assignees">
        <Popover content={() => <PeopleMenu selected={task.assigneeIds} onChange={(ids) => updateTask(task.id, { assigneeIds: ids })} />}>
          <button className="tm-value-btn">{task.assigneeIds.length ? <AvatarStack userIds={task.assigneeIds} size={24} max={5} /> : <span className="tm-empty">Empty</span>}</button>
        </Popover>
      </Prop>
      <Prop icon={<Calendar size={15} />} label="Dates">
        <div className="tm-dates">
          <Popover
            content={(close) => (
              <DateRangeMenu start={task.startDate} due={task.dueDate} initialFocus="start" onChange={(startDate, dueDate) => updateTask(task.id, { startDate, dueDate })} onClose={close} />
            )}
          >
            <button className="tm-value-btn">
              <CalendarClock size={14} className="faint" />
              {task.startDate ? <span>{formatDue(task.startDate)}</span> : <span className="tm-empty">Start</span>}
            </button>
          </Popover>
          <ArrowRight size={13} className="faint" />
          <Popover
            content={(close) => (
              <DateRangeMenu start={task.startDate} due={task.dueDate} initialFocus="due" onChange={(startDate, dueDate) => updateTask(task.id, { startDate, dueDate })} onClose={close} />
            )}
          >
            <button className={clsx('tm-value-btn', `due-${dueTone(task.dueDate, closed)}`)}>
              <Calendar size={14} className="faint" />
              {task.dueDate ? <span>{formatDue(task.dueDate)}</span> : <span className="tm-empty">Due</span>}
            </button>
          </Popover>
        </div>
      </Prop>
      <Prop icon={<Flag size={15} />} label="Priority">
        <Popover
          content={(close) => (
            <PriorityMenu
              value={task.priority}
              onPick={(p) => {
                updateTask(task.id, { priority: p });
                close();
              }}
            />
          )}
        >
          <button className="tm-value-btn">{task.priority ? <PriorityFlag priority={task.priority} showLabel /> : <span className="tm-empty">Empty</span>}</button>
        </Popover>
      </Prop>
      <Prop icon={<Hourglass size={15} />} label="Time estimate">
        <Popover content={(close) => <TimeEstimateEditor task={task} close={close} />}>
          <button className="tm-value-btn">{task.timeEstimate ? formatDuration(task.timeEstimate) : <span className="tm-empty">Empty</span>}</button>
        </Popover>
      </Prop>
      <Prop icon={<Timer size={15} />} label="Track time">
        <TrackTime task={task} />
      </Prop>
      <Prop icon={<Tag size={15} />} label="Tags">
        <Popover content={() => <TagMenu spaceId={spaceId} selected={task.tagIds} onChange={(ids) => updateTask(task.id, { tagIds: ids })} />}>
          <button className="tm-value-btn wrap">
            {task.tagIds.length ? task.tagIds.map((id) => <TagChip key={id} tagId={id} onRemove={() => updateTask(task.id, { tagIds: task.tagIds.filter((x) => x !== id) })} />) : <span className="tm-empty">Empty</span>}
          </button>
        </Popover>
      </Prop>
    </div>
  );
}
